import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddButton from "./AddButton";
import SuppliersList from "./SuppliersList";
import EmptySvg from "../../components/EmptySvg";
import Loader from "../../components/Loader";
import Pagination from "../ProductsList/Pagination";
import RenderPaginationNumbers from "../ProductsList/RenderPageNumber";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { API_URL } from "../../utils/config";
const baseUrl = API_URL;
const Suppliers = () => {
  const { t } = useTranslation("supplierlist");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDel, setIsDel] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { isAuthenticated, userRole } = useAuth();
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const go = () => navigate("add-new-supplier");

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("page", pageNumber.toString());
    window.history.replaceState(null, "", `?${urlParams.toString()}`);
  };

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  /* ---------------- URL params and pagination ---------------- */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const page = parseInt(urlParams.get("page")) || 1;
    setCurrentPage(page);
  }, []);

  /* ---------------- fetch suppliers ---------------- */
  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!isAuthenticated) {
        setError(new Error(t("errors.noToken")));
        setLoading(false);
        navigate("/login");
        return;
      }

      try {
        const params = new URLSearchParams({
          limit: itemsPerPage.toString(),
          page: currentPage.toString(),
        });

        const res = await fetch(`${baseUrl}/api/v1/attnd-supplier/suppliers?${params}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),

          },
        });

        const data = await res.json();

        if (!res.ok) {
          if (res.status === 401) {
            navigate("/login");
            throw new Error(t("errors.unauthorized"));
          }
          throw new Error(`An error occurred: ${data.error || res.statusText}`);
        }
        setSuppliers(data.data);
        setTotalPages(data.totalPages || 1);
        setTotalItems(data.totalItems || 0);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    !isDeleting && fetchSuppliers();
  }, [baseUrl, isDeleting, navigate, t, currentPage]);

  /* ---------------- delete supplier ---------------- */
  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      if (!isAuthenticated) return;

      const res = await fetch(`${baseUrl}/api/v1/attnd-supplier/suppliers/${isDel}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),

        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          navigate("/login");
          throw new Error(t("errors.unauthorized"));
        }
        const er = await res.json();
        throw new Error(er.error);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
      setIsDel(false);
    }
  };

  /* ------------- loading & fatal error ------------- */
  if (loading)
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );

  if (error)
    return (
      <div className="p-6">
        {t("errors.fetch")}: {error.message}
      </div>
    );

  /* ----------------- main UI ----------------------- */
  return (
    <div className="p-6">
      {/* ------- Header -------- */}
      <div className="pt-[24px] flex justify-between gap-[8px] relative">
        <div>
          <h3 className="w-[102px] h-[32px] font-inter font-black text-2xl leading-8 text-[#292929]">
            {t("header.title")}
          </h3>
          <p className="w-[289px] h-[44px] font-inter font-light text-base leading-[22px] text-[#464646]">
            {t("header.subtitlePrefix")}
            {suppliers.length === 0 && ` ${t("header.subtitleSuffix")}`}
          </p>
        </div>

        {/* Add button - only show if user is not viewer and there are suppliers */}
        {userRole !== 'viewer' && suppliers.length > 0 && <AddButton go={go} />}
      </div>

      {/* ------- Content / Empty state -------- */}
      {suppliers.length === 0 ? (
        <div className="flex flex-col gap-[24px] w-full h-full justify-center items-center">
          <EmptySvg />
          <h4 className="h-[29px] font-inter font-semibold text-2xl leading-[29px] text-[#292929]">
            {t("empty.title")}
          </h4>
          <p className="w-[459px] h-[44px] font-inter font-light text-sm text-center text-[#464646]">
            {t("empty.subtitle")}
          </p>
          {/* Add button in empty state - only show if user is not viewer */}
          {userRole !== 'viewer' && <AddButton go={go} />}
        </div>
      ) : (
        <SuppliersList suppliers={suppliers} del={setIsDel} />
      )}

      {/* ------- Pagination -------- */}
      {suppliers.length > 0 && totalPages > 1 && (
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        >
          <RenderPaginationNumbers
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        </Pagination>
      )}

      {/* ------- Delete modal -------- */}
      {isDel && (
        <div
          onClick={() => setIsDel(false)}
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
        >
          <div className="box-border flex flex-col items-center p-0 pt-1.5 fixed w-[393px] h-[305px] left-[calc(50%-196px)] top-[calc(50%-153px)] bg-white shadow-lg backdrop-filter backdrop-blur-[25px] rounded-t-lg">
            {/* Modal Header */}
            <div className="flex flex-row justify-between items-center p-2 px-4 w-full h-[46px]">
              <p className="m-0 font-inter font-semibold text-xl leading-[25px] text-[#292929]">
                {t("modal.deleteTitle")}
              </p>
              {/* Close icon */}
              <svg
                onClick={() => setIsDel(false)}
                style={{ cursor: "pointer" }}
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="30" height="30" rx="15" fill="#DBDBDB" />
                <path
                  d="M9.92188 20.4531C9.79167 20.3229 9.70573 20.1719 9.66406 20C9.6224 19.8229 9.6224 19.6484 9.66406 19.4766C9.70573 19.3047 9.78906 19.1562 9.91406 19.0312L13.5781 15.3672L9.91406 11.7109C9.78906 11.5859 9.70573 11.4375 9.66406 11.2656C9.6224 11.0938 9.6224 10.9219 9.66406 10.75C9.70573 10.5729 9.79167 10.4193 9.92188 10.2891C10.0469 10.1641 10.1953 10.0807 10.3672 10.0391C10.5443 9.99219 10.7188 9.99219 10.8906 10.0391C11.0677 10.0807 11.2188 10.1615 11.3438 10.2812L15 13.9453L18.6641 10.2891C18.7891 10.1641 18.9349 10.0807 19.1016 10.0391C19.2734 9.99219 19.4453 9.99219 19.6172 10.0391C19.7943 10.0807 19.9453 10.1667 20.0703 10.2969C20.2057 10.4219 20.2943 10.5729 20.3359 10.75C20.3776 10.9219 20.3776 11.0938 20.3359 11.2656C20.2943 11.4375 20.2083 11.5859 20.0781 11.7109L16.4297 15.3672L20.0781 19.0312C20.2083 19.1562 20.2943 19.3047 20.3359 19.4766C20.3776 19.6484 20.3776 19.8229 20.3359 20C20.2943 20.1719 20.2057 20.3203 20.0703 20.4453C19.9453 20.5755 19.7943 20.6641 19.6172 20.7109C19.4453 20.7526 19.2734 20.7526 19.1016 20.7109C18.9349 20.6693 18.7891 20.5833 18.6641 20.4531L15 16.7969L11.3438 20.4609C11.2188 20.5807 11.0677 20.6641 10.8906 20.7109C10.7188 20.7526 10.5443 20.7526 10.3672 20.7109C10.1953 20.6641 10.0469 20.5781 9.92188 20.4531Z"
                  fill="#464646"
                />
              </svg>
            </div>

            {/* Modal Body */}
            <div className="flex flex-col items-center py-4 w-full">
              <div className="flex flex-col items-center p-4 gap-2 w-[361px]">
                <h5 className="w-full font-inter font-bold text-base leading-6 text-[#292929] m-0">
                  {t("modal.question")}
                </h5>
                <p className="font-inter font-normal text-base leading-6 text-[#464646] m-0 text-center">
                  {t("modal.note")}
                </p>
              </div>
              <div className="flex flex-row justify-center gap-2 w-full p-4">
                <button
                  onClick={() => setIsDel(false)}
                  className="p-3.5 px-5 w-[176.5px] h-[48px] shadow-sm rounded-md font-inter text-base bg-white border border-gray-200 text-[#292929]"
                >
                  {t("modal.cancel")}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  className="p-3.5 px-5 w-[176.5px] h-[48px] shadow-sm rounded-md font-inter text-base bg-[#DC3545] text-white"
                >
                  {isDeleting ? t("modal.deleting") : t("modal.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;
